/**
 * Per-entity name projections: turn a stored record's name into the searchable
 * variants the resolver scores against. Pure and string-only — callers pull the
 * name off the record (contact.fullName, account.accountName, brand.name) and
 * pass it here, so this stays decoupled from the domain classes.
 *
 * The rule is the same everywhere: always keep the full name, and add a trimmed
 * variant when it helps a spoken mention line up (the rep drops the "Dr." or the
 * "Hospital"). Never strip so far that only a generic word is left.
 */

const HONORIFIC = /^(dr|doctor|prof|professor|mr|mrs|ms|miss|mx)\.?\s+/i;
const ACCOUNT_SUFFIX =
  /\s+(hospital|clinic|medical\s+cent(?:er|re)|health\s+cent(?:er|re)|pharmacy|ltd|inc|llc|gmbh|group|practice)\.?$/i;

function variants(name: string, stripped: string): string[] {
  const full = name.trim();
  if (!full) return [];
  const stem = stripped.trim();
  return stem && stem !== full ? [full, stem] : [full];
}

/** Drop a leading honorific ("Dr.", "Prof.", …) so a spoken title never blocks a match. */
export function stripHonorific(name: string): string {
  return (name ?? '').replace(HONORIFIC, '').trim();
}

export function contactNameVariants(fullName: string): string[] {
  const full = (fullName ?? '').trim();
  return variants(full, stripHonorific(full));
}

export function accountNameVariants(name: string): string[] {
  const full = (name ?? '').trim();
  const stem = full.replace(ACCOUNT_SUFFIX, '').trim();
  // Only offer the stem when it stays specific (more than one word).
  return stem.includes(' ') ? variants(full, stem) : variants(full, full);
}

export function productNameVariants(name: string): string[] {
  return variants((name ?? '').trim(), '');
}

export function keyMessageNameVariants(name: string): string[] {
  return variants((name ?? '').trim(), '');
}
